/**
 * @component LogoutButton
 * @description A button that logs the user out by clearing cookies, resetting the session, and redirecting to the login page.
 *
 * @param {LogoutProps} props - Component props.
 * @param {string} [props.className] - Optional additional CSS classes for styling.
 * @param {React.CSSProperties} [props.style] - Optional inline styles for the button.
 *
 * @returns {JSX.Element} A logout button.
 */

import React from "react"
import Cookies from "js-cookie"
import { container } from "../../../infrastructure/container"
import { SessionService } from "../../../infrastructure/services/session"
import { ApiService } from "../../../infrastructure/services/api"

interface LogoutProps {
    /**
   * Unique identifier for the button.
   */
    id: string

  /**
   * Additional CSS classes for custom styling.
   */
  className?: string

  /**
   * Inline styles for the button.
   */
  style?: React.CSSProperties
}

const LogoutButton: React.FC<LogoutProps> = ({ id, className, style }) => {
  const sessionService = container.resolve<SessionService>("sessionService")
  const apiService = container.resolve<ApiService>("apiService")

  const handleLogout = async () => {
    // Retrieve all cookies
    const allCookies = Cookies.get()

    // Remove all cookies
    Object.keys(allCookies).forEach((cookie) => {
      Cookies.remove(cookie, { path: "/", domain: "localhost" })
    })

    // Extra cleanup for cookies (redundant but ensures removal)
    for (const cookieName in allCookies) {
      if (Object.prototype.hasOwnProperty.call(allCookies, cookieName)) {
        Cookies.remove(cookieName)
      }
    }

    // Call API logout and reset session storage
    await apiService.logout()
    sessionService.resetStorage()

    // Redirect to login page
    window.location.href = "/login"
  }

  return (
    <button
      data-automation-id={id}
      id={id}
      onClick={handleLogout}
      className={className}
      style={style}
    >
      Logout
    </button>
  )
}

export default LogoutButton
